import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/Button";
import { useNetworxServices } from "../../../../../hooks/queries/useNetworxServices";
import { MeUser } from "../../../../../hooks/queries/useMe";
import CloseIcon from "../assets/CloseIcon";
import GuestMobileDrawerContent from "./GuestMobileDrawerContent";
import LoggedInMobileDrawerContent from "./LoggedInMobileDrawerContent";
import CollapsibleNavSection from "@/pages/homepage/components/common/CollapsibleNavSection";
import NavLink from "@/pages/homepage/components/common/NavLink";
import { prependCdnResourcesUrl } from "@/utils/network";

export default function MobileDrawer({ isOpen, onClose, user }: MobileDrawerProps) {
  const { data } = useNetworxServices({
    sortBy: "alphabetical",
  });
  const services = data?.services ?? [];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-white"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed inset-x-0 top-0 z-50 flex h-dvh flex-col overflow-hidden bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Logo and Close Button */}
            <div className="flex shrink-0 items-center justify-between">
              {/* Logo */}
              <div className="flex lg:flex-1 p-4">
                <a href="/">
                  <img
                    src={prependCdnResourcesUrl("/images/global/networx-logo-v2.svg")}
                    alt="Networx"
                    className="h-7 xl:h-8 w-auto"
                  />
                </a>
              </div>
              <div className="flex-1" />
              <Button
                isIconOnly
                color="default"
                variant="light"
                radius="full"
                className="h-11 w-11 min-w-11 pr-1.5"
                onPress={onClose}
                aria-label="Close menu"
              >
                <CloseIcon className="h-5 w-5" />
              </Button>
            </div>

            {/* Navigation Content */}
            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain pb-1 pt-4">
              {/* Find a Pro dropdown */}
              <CollapsibleNavSection
                label="Find a Pro"
                classNames={{
                  wrapper: "pt-1 pb-3",
                }}
              >
                {services.map((service) => (
                  <NavLink
                    key={service.id}
                    href={service.pathname}
                    onClick={onClose}
                    variant="nested"
                  >
                    {service.display_name}
                  </NavLink>
                ))}
              </CollapsibleNavSection>

              {/* Other navigation links + login/logout buttons */}
              {user?.isLoggedIn ? (
                <LoggedInMobileDrawerContent user={user} onClose={onClose} />
              ) : (
                <GuestMobileDrawerContent
                  onClose={onClose}
                />
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

interface MobileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  user: MeUser | undefined;
}
